import {
    LookupSecuritiesBySymbolResponse,
    SearchSecurityResponse,
    Securities,
    Security,
    SecurityCategories
} from '../../proto/generated/security'
import overviewActions from '../../actions/overview.actions'
import api from './axiosClient'
import { Exchange, exchangeFromJSON, exchangeToJSON } from '../../proto/generated/common'
import { AppDispatch } from '../../store'
import {
    GetIndexComponentsPageResponse,
    GetTechnicalsConfigResponse,
    OverviewWidgetResponse
} from '../../proto/generated/overview-widget'
import { getSecurityKey, isValidExchange } from '../../util/security'

let categoriesPromise: Promise<SecurityCategories> | null = null
let categoriesRequestPending = false
/** Last resolved categories, so a freshly mounted Symbol Search can render its chips and labels synchronously. */
let cachedCategories: SecurityCategories | null = null

export const getCachedSecurityCategories = (): SecurityCategories | null => cachedCategories

export const getCategories = async (forceRefresh = false): Promise<SecurityCategories> => {
    if (forceRefresh && !categoriesRequestPending) {
        categoriesPromise = null
    }
    if (!categoriesPromise) {
        categoriesRequestPending = true
        categoriesPromise = api
            .get(`/securities/categories`, {
                headers: {
                    Accept: 'application/x-protobuf',
                },
                responseType: 'arraybuffer',
            })
            .then((response) => {
                cachedCategories = SecurityCategories.decode(new Uint8Array(response.data))
                return cachedCategories
            })
            .catch((error) => {
                categoriesPromise = null
                throw error
            })
            .finally(() => {
                categoriesRequestPending = false
            })
    }
    return categoriesPromise
}

/** Warm the categories cache so Symbol Search opens without waiting on the network. */
export const prefetchSecurityCategories = (): void => {
    void getCategories().catch(() => {
        /* ignore — next open will retry */
    })
}

export const fetchSecurities = (securities: {exchange: Exchange, code: string}[]): Promise<Security[]> => {
    return new Promise((resolve, reject) => {
        securityFetchBatch.push(...securities)
        securityFetchWaiters.push({ requested: securities, resolve, reject })
        if (securityFetchTimer) clearTimeout(securityFetchTimer)
        securityFetchTimer = setTimeout(() => {
            void flushSecurityFetchBatch()
        }, 30)
    })
}

type SecurityFetchWaiter = {
    requested: { exchange: Exchange, code: string }[]
    resolve: (securities: Security[]) => void
    reject: (error: unknown) => void
}

let securityFetchBatch: { exchange: Exchange, code: string }[] = []
let securityFetchWaiters: SecurityFetchWaiter[] = []
let securityFetchTimer: ReturnType<typeof setTimeout> | null = null

const securityFetchKey = (security: { exchange: Exchange, code: string }) => `${security.exchange}:${security.code}`

async function flushSecurityFetchBatch() {
    const requested = securityFetchBatch
    const pendingWaiters = securityFetchWaiters
    securityFetchBatch = []
    securityFetchWaiters = []
    securityFetchTimer = null

    const unique = new Map<string, { exchange: Exchange, code: string }>()
    requested.forEach((security) => {
        unique.set(securityFetchKey(security), security)
    })

    try {
        const response = await api.post('/v2/security', JSON.stringify({ securities: [...unique.values()] }), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        })
        const decodedData = Securities.decode(new Uint8Array(response.data))
        pendingWaiters.forEach((waiter) => {
            const wanted = new Set(waiter.requested.map(securityFetchKey))
            waiter.resolve(decodedData.securities.filter((security) => wanted.has(securityFetchKey(security))))
        })
    } catch (error) {
        pendingWaiters.forEach((waiter) => waiter.reject(error))
    }
}

export const lookupSecurities = async (args: {
    pageNumber: number
    categoryCode: string
    exchange?: string
    searchText: string
    pageSize: number
    hasFo?: boolean
    instrument?: string
}): Promise<SearchSecurityResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/securities/lookup`
        api.post(url, JSON.stringify(args), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = SearchSecurityResponse.decode(new Uint8Array(response.data))

            resolve(decodedData)
        }).catch((error) => {

            reject(error)
        })
    })
}

export const getOverviewWidget = async (args: {
    exchange: Exchange,
    code: string,
    dispatch: AppDispatch
}): Promise<OverviewWidgetResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/securities/overview`
        api.post(url, JSON.stringify({
            security: {
                exchange: args.exchange,
                code: args.code
            }
        }), {
            headers: {
                'Accept': 'application/x-protobuf'
            },

            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = OverviewWidgetResponse.decode(new Uint8Array(response.data))
            const securityKey = getSecurityKey(args.exchange, args.code)
            overviewActions.dispatchOverviewData(
                args.dispatch,
                securityKey,
                decodedData.fieldGroups,
                decodedData.indexComponents,
                decodedData.performance,
                decodedData.communityScreens,
                decodedData.indexComponentTotalCount,
                decodedData.hasIndexContribution,
            )
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getIndexComponentsPage = async (args: {
    exchange: Exchange
    code: string
    page: number
    pageSize?: number
}): Promise<GetIndexComponentsPageResponse> => {
    const response = await api.post(
        `/securities/index-components`,
        JSON.stringify({
            indexSecurity: {
                exchange: args.exchange,
                code: args.code
            },
            page: args.page,
            pageSize: args.pageSize ?? 2000
        }),
        {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }
    )
    return GetIndexComponentsPageResponse.decode(new Uint8Array(response.data))
}

export const getTechnicalsConfig = async (): Promise<GetTechnicalsConfigResponse> => {
    const response = await api.post(
        `/securities/technicals/config`,
        JSON.stringify({}),
        {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }
    )
    return GetTechnicalsConfigResponse.decode(new Uint8Array(response.data))
}

export const lookupSymbols = async (args: Array<{ exchange: Exchange | number | string; symbol: string }>) => {
    const url = '/securities/symbol-lookup'
    const securities = args
        .map(({ exchange, symbol }) => ({
            exchange: typeof exchange === 'string' || typeof exchange === 'number' ? exchangeFromJSON(exchange) : exchange,
            symbol,
        }))
        .filter(({ exchange }) => isValidExchange(exchange))

    if (!securities.length) {
        return LookupSecuritiesBySymbolResponse.fromPartial({ securities: [] })
    }

    const response = await api.post(
        url,
        JSON.stringify({
            securities: securities.map((v) => ({
                exchange: exchangeToJSON(v.exchange),
                symbol: v.symbol,
            })),
        }),
        {
            headers: {
                'Accept': 'application/x-protobuf'
            },

            responseType: 'arraybuffer'
        })

    return LookupSecuritiesBySymbolResponse.decode(new Uint8Array(response.data))

}